import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Building2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, EmptyState, Loading } from "@/components/common";
import { FormDialog } from "@/components/FormDialog";
import { api, useFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FALLBACK = "https://images.unsplash.com/photo-1618660920685-4505debb785a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export function propertyFields(landlords, isAgent) {
  return [
    { name: "name", label: "Property name", required: true, full: true, placeholder: "e.g. 12 Elm Terrace" },
    { name: "address_line", label: "Address", full: true },
    { name: "city", label: "Town / city" },
    { name: "postcode", label: "Postcode" },
    { name: "property_type", label: "Type", type: "select", default: "house",
      options: ["house", "flat", "hmo", "bungalow", "commercial", "other"].map((t) => ({ value: t, label: t.toUpperCase() === "HMO" ? "HMO" : t[0].toUpperCase() + t.slice(1) })) },
    { name: "country", label: "Country", default: "United Kingdom" },
    ...(isAgent ? [{ name: "landlord_id", label: "Landlord", type: "select", nullable: true, noneLabel: "Unassigned",
      options: (landlords || []).map((l) => ({ value: l.id, label: l.name })), full: true }] : []),
    { name: "image_url", label: "Photo URL (optional)", full: true },
    { name: "notes", label: "Notes", type: "textarea" },
  ];
}

export default function Properties() {
  const { session } = useAuth();
  const isAgent = session.business.type === "agent";
  const { data, reload } = useFetch("/biz/properties");
  const { data: landlords } = useFetch(isAgent ? "/biz/landlords" : null);
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="properties-page">
      <PageHeader eyebrow="Portfolio" title="Properties" subtitle="Your homes, flats and HMOs — with units, tenancies and certificates."
        actions={<Button onClick={() => setOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-property-button"><Plus className="w-4 h-4 mr-1" />Add property</Button>} />
      {!data ? <Loading /> : data.length === 0 ? (
        <EmptyState icon={Building2} title="No properties yet" text="Add your first property to start tracking tenancies, rent and compliance." />
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {data.map((p) => (
            <Link key={p.id} to={`/app/properties/${p.id}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden lift" data-testid={`property-card-${p.id}`}>
              <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                <img src={p.image_url || FALLBACK} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-lg font-bold leading-tight">{p.name}</h3>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${p.occupied >= p.spaces ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                    {p.occupied}/{p.spaces} let
                  </span>
                </div>
                <div className="text-sm text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[p.address_line, p.city, p.postcode].filter(Boolean).join(", ") || "No address"}</div>
                <div className="text-xs text-slate-500 mt-3 flex gap-3">
                  <span className="uppercase tracking-wider">{p.property_type}</span>
                  <span>{p.unit_count} units</span>
                  {p.landlord_name && <span>· {p.landlord_name}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      <FormDialog open={open} onOpenChange={setOpen} title="Add property" testid="property-form" fields={propertyFields(landlords, isAgent)}
        onSubmit={async (v) => { await api.post("/biz/properties", v); toast.success("Property added"); reload(); }} />
    </div>
  );
}
